import { ChevronDownIcon } from "@chakra-ui/icons";
import { Box, Collapse, Flex, Tooltip, useDisclosure } from "@chakra-ui/react";
import type { JSONSchema7 } from "json-schema";
import { ReactNode, useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { RanaAlert, RanaButton, RanaHeading, RanaMarkdown } from "@nens/ranalib";
import { useRect } from "@nens/react/hooks";

import { HeaderBox } from "@/components/header-box";

type IgnoreAny = any; // eslint-disable-line @typescript-eslint/no-explicit-any

const getJsonStr = (object: IgnoreAny) => {
  const jsonStr = JSON.stringify(
    object,
    (key, value) => (value === undefined ? "__UNDEF__" : value),
    2
  );
  return jsonStr.replace(/"__UNDEF__"/g, "undefined");
};
export const RootResultError = ({
  children,
  propertyKey,
  property,
  values,
}: {
  children: ReactNode;
  propertyKey: string;
  property: JSONSchema7;
  values: { [key: string]: IgnoreAny };
}) => {
  const intl = useIntl();
  const parentRef = useRef<HTMLDivElement>(null);
  const alertRef = useRef<HTMLDivElement>(null);
  const alertRect = useRect(alertRef, true);

  const { isOpen, onToggle } = useDisclosure();

  const json = getJsonStr({
    property: { [propertyKey]: property },
    values,
  });

  const detailExplenation = intl.formatMessage({
    defaultMessage: `This shows the JSON representation of the property and all the values of the processes results. You can use this to debug the process results and see what is going wrong. For example if the key of the property is "raster" and the key of the value is "DEM", then that mismatch is the reason why the result of the property is not found in the process results.`,
    id: "processresults.debug_explanation",
  });

  return (
    <Box mb={2}>
      <HeaderBox
        flexDirection="column"
        name={intl.formatMessage(
          {
            defaultMessage: `Error for result of "{title}"`,
            id: "processresults.error_for_property",
          },
          { title: property.title }
        )}
      >
        <Flex
          ref={parentRef}
          alignItems="center"
          w="full"
          flexDir="column"
          justifyContent="center"
          px={8}
          py={4}
          h="full"
        >
          <Flex ref={alertRef} mb={4} flexDir="column">
            <Collapse in={!isOpen}>
              <RanaAlert transition="all 0.2s" w="fit" mt={4} flexShrink={0} status="error">
                {children}
              </RanaAlert>
            </Collapse>
          </Flex>
          <Flex
            width={isOpen ? parentRef.current?.clientWidth : alertRect.width}
            px={isOpen ? 8 : 0}
            transition="width 0.2s, padding 0.2s"
            justifyContent="space-between"
            flexShrink={0}
            mb={4}
          >
            <Tooltip label={detailExplenation}>
              <Box
                overflow="hidden"
                opacity={isOpen ? 1 : 0}
                pointerEvents={isOpen ? "all" : "none"}
                transition="all 0.2s"
                maxW="prose"
              >
                <RanaHeading
                  textOverflow="ellipsis"
                  overflow="hidden"
                  whiteSpace="nowrap"
                  type="h6"
                  pr={4}
                >
                  {detailExplenation}
                </RanaHeading>
              </Box>
            </Tooltip>
            <RanaButton
              minW="fit"
              variant="fourthiary"
              onClick={onToggle}
              leftIcon={
                isOpen ? <ChevronDownIcon /> : <ChevronDownIcon transform="rotate(-90deg)" />
              }
            >
              {isOpen ? (
                <FormattedMessage id="processresults.hide_details" defaultMessage="Hide Details" />
              ) : (
                <FormattedMessage id="processresults.show_details" defaultMessage="Show Details" />
              )}
            </RanaButton>
          </Flex>

          <Box overflow="auto" w="full">
            <Collapse in={isOpen}>
              <RanaMarkdown markdown={"```json\n" + json + "\n```"} />
            </Collapse>
          </Box>
        </Flex>
      </HeaderBox>
    </Box>
  );
};
